
import { useState } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Upload, Trash2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useTranslations } from "@/hooks/useTranslations";

interface ProfileAvatarProps {
  profile: any;
  user: any;
}

export const ProfileAvatar = ({ profile, user }: ProfileAvatarProps) => {
  const { t } = useTranslations("settings");

  const handleAvatarUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      if (!event.target.files || event.target.files.length === 0) return;
      
      const file = event.target.files[0];
      const fileExt = file.name.split('.').pop();
      const filePath = `${user?.id}/avatar.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, file, { upsert: true });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath);

      const { error: updateError } = await supabase
        .from('profiles')
        .update({ avatar_url: publicUrl })
        .eq('id', user?.id);

      if (updateError) throw updateError;

      toast.success(t("profile.photoSuccess"));
    } catch (error) {
      console.error('Error uploading avatar:', error);
      toast.error(t("profile.photoError"));
    }
  };

  const handleRemoveAvatar = async () => {
    try {
      if (!profile?.avatar_url) return;

      const { error: updateError } = await supabase
        .from('profiles')
        .update({ avatar_url: null })
        .eq('id', user?.id);

      if (updateError) throw updateError;

      toast.success(t("profile.photoRemoved"));
    } catch (error) {
      console.error('Error removing avatar:', error);
      toast.error(t("profile.photoErrorDesc"));
    }
  };

  return (
    <div className="flex flex-col items-center space-y-4">
      <Avatar className="h-24 w-24">
        <AvatarImage src={profile?.avatar_url || ""} />
        <AvatarFallback>
          {profile?.first_name?.[0]}{profile?.last_name?.[0]}
        </AvatarFallback>
      </Avatar>
      <div className="flex flex-wrap gap-2 justify-center w-full">
        <Button
          type="button"
          variant="outline"
          onClick={() => document.getElementById('avatar-upload')?.click()}
          className="flex-1 min-w-[140px] max-w-[200px]"
        >
          <Upload className="mr-2 h-4 w-4" />
          {t("profile.uploadPhoto")}
        </Button>
        {profile?.avatar_url && (
          <Button
            type="button"
            variant="destructive"
            onClick={handleRemoveAvatar}
            className="flex-1 min-w-[140px] max-w-[200px]"
          >
            <Trash2 className="mr-2 h-4 w-4" />
            {t("profile.removePhoto")}
          </Button>
        )}
        <Input
          id="avatar-upload"
          type="file"
          accept="image/*"
          onChange={handleAvatarUpload}
          className="hidden"
        />
      </div>
    </div>
  );
};
